import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  InfoCallout,
  Input,
  Notice,
  RadioCard,
  RadioCardGroup,
  Row,
  Stack,
  Textarea,
} from '@leanin/ui';
import { Sprout } from 'lucide-react';
import type { CircleFormat } from '@leanin/shared/circle-format';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import { useOnboardingProgress } from '@/components/onboarding/progress';
import {
  readCreatedCircleId,
  writeCreatedCircleId,
} from '@/components/onboarding/created-circle';
import { circleSaveErrorMessage } from '@/components/circles/circle-save-error';
import { useErrorMessage } from '@/lib/error-message';
import { useCapabilities } from '@/lib/use-session';
import {
  CircleLeaderFactsFields,
  NO_CIRCLE_LEADER_FACTS,
  parseCircleLeaderFacts,
  type CircleLeaderFacts,
  type CircleLeaderFactsErrors,
  useDiscussionTopics,
} from '@/components/circles/CircleLeaderFactsFields';
import { useFocusFirstError } from '@/components/form/use-focus-first-error';

// Circle Leader onboarding tour — step 2 of 3.
//
// Submitting this step creates a real Circle row server-side with the
// caller as the leader. A successful create persists the new circle id
// into sessionStorage (created-circle.ts); if the leader bounces back to
// this step later, Next short-circuits to navigate-only so we don't
// double-create. (`affiliated`/`company` are collected for parity with
// /circles/new but not yet persisted — schema work pending.)
const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: "Your Circle's page" },
  { label: "You're in" },
];

type YesNo = 'yes' | 'no' | null;

type RequiredField = 'name' | 'affiliated' | 'company' | 'openToNew';

const NONE_MISSING: ReadonlySet<RequiredField> = new Set();

type RequiredAnswers = {
  readonly name: string;
  readonly affiliation:
    | { readonly affiliatedWithCompany: true; readonly affiliatedCompany: string }
    | { readonly affiliatedWithCompany: false; readonly affiliatedCompany: null };
  readonly joinPolicy: 'request' | 'invite_only';
};

// Location is optional on this step, unlike /circles/new.
function parseRequiredAnswers(d: {
  readonly name: string;
  readonly affiliated: YesNo;
  readonly company: string;
  readonly openToNew: YesNo;
}):
  | { readonly ok: true; readonly value: RequiredAnswers }
  | { readonly ok: false; readonly missing: ReadonlySet<RequiredField> } {
  const name = d.name.trim();
  const company = d.company.trim();
  const affiliation =
    d.affiliated === 'no'
      ? { affiliatedWithCompany: false as const, affiliatedCompany: null }
      : d.affiliated === 'yes' && company.length > 0
        ? { affiliatedWithCompany: true as const, affiliatedCompany: company }
        : null;
  if (name.length > 0 && affiliation !== null && d.openToNew !== null) {
    return {
      ok: true,
      // "Open to new members? Yes" → request-to-join; "No" → invite-only.
      value: { name, affiliation, joinPolicy: d.openToNew === 'yes' ? 'request' : 'invite_only' },
    };
  }
  return {
    ok: false,
    missing: new Set<RequiredField>([
      ...(name.length === 0 ? (['name'] as const) : []),
      ...(d.affiliated === null ? (['affiliated'] as const) : []),
      ...(d.affiliated === 'yes' && company.length === 0 ? (['company'] as const) : []),
      ...(d.openToNew === null ? (['openToNew'] as const) : []),
    ]),
  };
}

export const Route = createFileRoute('/onboarding/cl/create-circle')({
  component: CLOnboardingCreateCirclePage,
});

function CLOnboardingCreateCirclePage() {
  const navigate = useNavigate();
  const describeError = useErrorMessage();
  const createCircle = useCreateCircle();
  // An invited circle leader already leads the Circle they were invited to
  // (redeemed pre-onboarding by the Clerk webhook). This step creates a NEW
  // Circle, so for them it would make a duplicate — skip straight to the
  // completion screen, which deep-links to their actual Circle. A self-select
  // leader hasn't created a Circle yet, so `leaderCircleId` is null and they
  // see the form as normal.
  const { data: onboardingProgress } = useOnboardingProgress();
  const alreadyLeadsCircle = onboardingProgress?.leaderCircleId != null;
  useEffect(() => {
    if (alreadyLeadsCircle) {
      navigate({ to: '/onboarding/cl/complete' });
    }
  }, [alreadyLeadsCircle, navigate]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [affiliated, setAffiliated] = useState<YesNo>(null);
  const [company, setCompany] = useState('');
  const [openToNew, setOpenToNew] = useState<YesNo>(null);
  const [format, setFormat] = useState<CircleFormat>('virtual');
  const [leaderFacts, setLeaderFacts] = useState<CircleLeaderFacts>(NO_CIRCLE_LEADER_FACTS);
  const [leaderFactsErrors, setLeaderFactsErrors] = useState<CircleLeaderFactsErrors>({});
  const profileFields = useCapabilities()?.circleProfileFields === true;
  const discussionTopics = useDiscussionTopics({ enabled: profileFields });
  const [error, setError] = useState<string | null>(null);
  const [createdCircleId, setCreatedCircleId] = useState<string | null>(
    () => readCreatedCircleId('cl-tour'),
  );

  // "Save and continue" stays enabled: a click with a required answer missing
  // says so and takes the leader to the first one. Format defaults to
  // 'virtual', so it is never missing.
  const [continueAttempted, setContinueAttempted] = useState(false);
  const required = parseRequiredAnswers({ name, affiliated, company, openToNew });
  const missing = continueAttempted && !required.ok ? required.missing : NONE_MISSING;
  const formMessage = error ?? (missing.size > 0 ? 'Please fill in every required field to continue.' : null);
  const fieldsRef = useRef<HTMLElement | null>(null);
  const focusFirstError = useFocusFirstError(fieldsRef);

  const isPending = createCircle.isPending;

  const handleNext = () => {
    if (isPending) return;
    setError(null);
    // A Circle already created in this session (the leader came back to this
    // step): advance without re-validating; the commit already happened.
    if (createdCircleId !== null) {
      // Already created in this onboarding session — just advance. Still a
      // step completion for the funnel: the user moved past this step.
      trackOnboardingStepCompleted(
        flowStepParams('/onboarding/cl/create-circle'),
      );
      navigate({ to: '/onboarding/cl/complete' });
      return;
    }
    setContinueAttempted(true);
    const facts = parseCircleLeaderFacts(leaderFacts, discussionTopics.activeIds);
    setLeaderFactsErrors(facts.ok ? {} : facts.errors);
    if (!required.ok || !facts.ok) {
      focusFirstError();
      return;
    }
    createCircle.mutate(
      {
        name: required.value.name,
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
        place,
        topicIds: facts.value.topicIds,
        format,
        ...(facts.value.meetingCadence !== null ? { meetingCadence: facts.value.meetingCadence } : {}),
        ...(facts.value.targetSize !== null ? { targetSize: facts.value.targetSize } : {}),
        access: 'public',
        joinPolicy: required.value.joinPolicy,
        ...required.value.affiliation,
      },
      {
        onSuccess: (detail) => {
          writeCreatedCircleId('cl-tour', detail.id);
          setCreatedCircleId(detail.id);
          trackOnboardingStepCompleted(
            flowStepParams('/onboarding/cl/create-circle'),
          );
          navigate({ to: '/onboarding/cl/complete' });
        },
        onError: (err) => {
          setError(circleSaveErrorMessage(err, describeError, 'Failed to create your Circle.'));
        },
      },
    );
  };

  return (
    <FlowShell
      role="Circle Leader"
      stepIndex={1}
      steps={STEPS}
      minutes={3}
      title="Tell us about your Circle."
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          loading={isPending}
          onClick={handleNext}
        >
          Save and continue
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/cl/welcome' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg" ref={fieldsRef}>
        <InfoCallout tone="lilac" pad="default">
          <Row gap="xl" align="center">
            <div
              aria-hidden
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--radius-full)',
                background: 'rgba(255, 255, 255, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'var(--palette-navy)',
              }}
            >
              <Sprout size={26} strokeWidth={1.75} />
            </div>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Heading level={3}>Tell us a little about your Circle.</Heading>
              <Body size="sm" color="mute">
                You can update everything later, even your Circle name.
              </Body>
            </Stack>
          </Row>
        </InfoCallout>

        <Card pad="compact">
          <Stack gap="sm">
            <Eyebrow>Basic details</Eyebrow>
            <Field required>
              <Field.Label>Circle name</Field.Label>
              <Field.Control>
                <Input
                  aria-invalid={missing.has('name') || undefined}
                  placeholder="Give your Circle a name."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>Description</Field.Label>
              <Field.Control>
                <Textarea
                  rows={2}
                  placeholder="What is the Circle about? Who is it for?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>Location</Field.Label>
              <Field.Control>
                <PlacePicker
                  mode="city"
                  value={place}
                  onChange={setPlace}
                  placeholder="Where do you expect your Circle to meet?"
                />
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>Is your Circle affiliated with a company?</Field.Label>
              <Field.Description>
                Select this option if your Circle is part of a formal program
                rolled out by your organization or because your Circle is made
                up of people from the same organization.
              </Field.Description>
              <Field.Control>
                <RadioCardGroup
                  fold="never"
                  value={affiliated ?? ''}
                  onValueChange={(v) => setAffiliated(v as 'yes' | 'no')}
                  columns={2}
                  aria-label="Is your Circle affiliated with a company?"
                  aria-invalid={missing.has('affiliated')}
                >
                  <RadioCard value="yes" title="Yes" />
                  <RadioCard value="no" title="No" />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            {affiliated === 'yes' ? (
              <Field label="Which company?" required>
                <Input
                  aria-invalid={missing.has('company') || undefined}
                  placeholder="e.g. Acme Inc."
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Field>
            ) : null}
            <Field required>
              <Field.Label>Is this Circle open to new members?</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  fold="never"
                  value={openToNew ?? ''}
                  onValueChange={(v) => setOpenToNew(v as 'yes' | 'no')}
                  columns={2}
                  aria-label="Is this Circle open to new members?"
                  aria-invalid={missing.has('openToNew')}
                >
                  <RadioCard value="yes" title="Yes" />
                  <RadioCard value="no" title="No" />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            <Field label="How will your Circle meet?" required>
              <RadioCardGroup
                fold="never"
                value={format}
                onValueChange={(v) => setFormat(v as CircleFormat)}
                columns={3}
                aria-label="Meeting format"
              >
                <RadioCard value="virtual" icon={I.globe} title="Virtually" />
                <RadioCard value="in_person" icon={I.pin} title="In-person" />
                <RadioCard value="hybrid" icon={I.users} title="Hybrid" />
              </RadioCardGroup>
            </Field>
            {profileFields ? (
              <CircleLeaderFactsFields
                value={leaderFacts}
                onChange={(next) => {
                  setLeaderFacts(next);
                  setLeaderFactsErrors({});
                }}
                errors={leaderFactsErrors}
                topics={discussionTopics}
                disabled={isPending}
              />
            ) : null}
          </Stack>
        </Card>

        {formMessage !== null ? (
          <Notice intent="danger" dismissible={false} title={formMessage} />
        ) : null}
      </Stack>
    </FlowShell>
  );
}
